export type AutosaveResult = boolean | void;

/**
 * Coalesces delayed response updates while still allowing final submission to
 * wait for the latest value to reach the API.
 */
export class FlushableAutosave<T> {
  private pendingValue: T | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private queue: Promise<boolean> = Promise.resolve(true);

  constructor(
    private readonly save: (value: T) => Promise<AutosaveResult>,
    private readonly delay: number,
  ) {}

  schedule(value: T) {
    this.pendingValue = value;
    this.clearTimer();
    this.timer = setTimeout(() => {
      void this.flush();
    }, this.delay);
  }

  saveNow(value: T) {
    this.pendingValue = null;
    this.clearTimer();
    return this.enqueue(value);
  }

  flush() {
    const pendingValue = this.pendingValue;
    this.pendingValue = null;
    this.clearTimer();

    return pendingValue === null ? this.queue : this.enqueue(pendingValue);
  }

  dispose() {
    return this.flush();
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private enqueue(value: T) {
    this.queue = this.queue.then(async () => {
      try {
        return (await this.save(value)) !== false;
      } catch (_error) {
        return false;
      }
    });

    return this.queue;
  }
}

export const submitAfterAutosaves = async ({
  flush,
  submit,
}: {
  flush: () => Promise<boolean>;
  submit: () => Promise<boolean>;
}) => {
  if (!(await flush())) return false;
  return submit();
};
